// External packages
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// Types
import type {
  Image,
  Audio,
  Video,
  PlaceMap,
} from '../../../server/src/types/media.types';

// Hooks
import { useToast } from 'hooks/useToast';
import { API_BASE_URL, authHeaders } from './base';

type MediaType = 'images' | 'videos' | 'audio' | 'placeMaps';

type MediaTypeMap = {
  images: Image;
  videos: Video;
  audio: Audio;
  placeMaps: PlaceMap;
};

// Translation types
type Translation = {
  id?: string;
  languageCode: string;
  description: string;
  imageId?: string;
};

type TranslationPayload = {
  translations: Omit<Translation, 'id' | 'imageId'>[];
};

export const uploadMedia = async (
  fileType: MediaType,
  file: File,
  authToken: string = localStorage.getItem('authToken') || ''
) => {
  const formData = new FormData();

  formData.append('file', file);

  try {
    const response = await fetch(
      `${API_BASE_URL}/${fileType}/upload`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        body: formData,
        cache: 'no-cache',
      }
    );
    if (!response.ok)
      throw new Error(`Failed to create exhibit: ${response.statusText}`);

    return response.json();
  } catch (error) {
    console.error(error);
    throw new Error('Error uploading media');
  }
};

export const useUploadMedia = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ file, fileType }: { file: File; fileType: MediaType }) => {
      return uploadMedia(fileType, file);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
      const mediaTypeLabels = {
        images: 'Slika',
        videos: 'Video',
        audio: 'Audio',
        placeMaps: 'Mapa',
      };
      const label = mediaTypeLabels[variables.fileType] || 'Datoteka';
      toast.success(`${label} je uspješno uploadana!`);
    },
    onError: (error: any, variables) => {
      console.error('Failed to upload media:', error);
      const mediaTypeLabels = {
        images: 'slike',
        videos: 'videa',
        audio: 'audio datoteke',
        placeMaps: 'mape',
      };
      const label = mediaTypeLabels[variables.fileType] || 'datoteke';
      toast.error(error?.message || `Greška pri uploadu ${label}.`);
    },
  });
};

export const getAllMedia = async <T extends MediaType>(
  fileType: T
): Promise<MediaTypeMap[T][]> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/${fileType}`,
      {
        method: 'GET',
        headers: authHeaders(),
        cache: 'no-cache',
      }
    );

    if (!response.ok)
      throw new Error(`Failed to fetch media: ${response.statusText}`);

    const result = await response.json();

    return result.data as MediaTypeMap[T][];
  } catch (error) {
    console.error(error);
    throw new Error('Error fetching media');
  }
};

export const useGetAllMedia = <T extends MediaType>(fileType: T) => {
  return useQuery({
    queryKey: ['media', fileType],
    queryFn: () => getAllMedia(fileType),
    initialData: [] as MediaTypeMap[T][],
  });
};

export const deleteMedia = async (
  fileType: MediaType,
  id: string,
  authToken: string = localStorage.getItem('authToken') || ''
) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/${fileType}/${id}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        cache: 'no-cache',
      }
    );
    if (!response.ok)
      throw new Error(`Failed to delete media: ${response.statusText}`);

    return response.json();
  } catch (error) {
    console.error(error);
    throw new Error('Error deleting media');
  }
};

export const useDeleteMedia = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ fileType, id }: { fileType: MediaType; id: string }) =>
      deleteMedia(fileType, id),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
      const mediaTypeLabels = {
        images: 'Slika',
        videos: 'Video',
        audio: 'Audio',
        placeMaps: 'Mapa',
      };
      const label = mediaTypeLabels[variables.fileType] || 'Datoteka';
      toast.success(`${label} je uspješno obrisana!`);
    },
    onError: (error: any, variables) => {
      console.error('Failed to delete media:', error);
      const mediaTypeLabels = {
        images: 'slike',
        videos: 'videa',
        audio: 'audio datoteke',
        placeMaps: 'mape',
      };
      const label = mediaTypeLabels[variables.fileType] || 'datoteke';
      toast.error(error?.message || `Greška pri brisanju ${label}.`);
    },
  });
};

// Image Translation functions
export const addImageTranslations = async (
  imageId: string,
  translations: TranslationPayload,
  authToken: string = localStorage.getItem('authToken') || ''
) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/images/${imageId}/translations`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(translations),
        cache: 'no-cache',
      }
    );

    if (!response.ok)
      throw new Error(`Failed to add translations: ${response.statusText}`);

    return response.json();
  } catch (error) {
    console.error(error);
    throw new Error('Error adding image translations');
  }
};

export const useAddImageTranslations = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({
      imageId,
      translations,
    }: {
      imageId: string;
      translations: TranslationPayload;
    }) => addImageTranslations(imageId, translations),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
      queryClient.invalidateQueries({ queryKey: ['image-translations'] });
      toast.success('Prijevodi su uspješno dodani!');
    },
    onError: (error: any) => {
      console.error('Failed to add image translations:', error);
      toast.error(error?.message || 'Greška pri dodavanju prijevoda.');
    },
  });
};

export const getImageTranslations = async (
  imageId: string,
  authToken: string = localStorage.getItem('authToken') || ''
): Promise<Translation[]> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/images/${imageId}/translations`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        cache: 'no-cache',
      }
    );

    if (!response.ok)
      throw new Error(`Failed to fetch translations: ${response.statusText}`);

    const result = await response.json();
    return result.data as Translation[];
  } catch (error) {
    console.error(error);
    throw new Error('Error fetching image translations');
  }
};

export const useGetImageTranslations = (imageId: string) => {
  return useQuery({
    queryKey: ['image-translations', imageId],
    queryFn: () => getImageTranslations(imageId),
    initialData: [] as Translation[],
    enabled: !!imageId,
  });
};
